import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productAttributesApi, type Attribute, type AttributeType, type AttributesDashboard } from '../../api/productattributes';

const NAV = [
  { to: '/product-attributes/library', label: 'Attribute library' },
  { to: '/product-attributes/builder', label: 'Attribute builder' },
  { to: '/product-attributes/sets', label: 'Attribute sets' },
  { to: '/product-attributes/templates', label: 'Templates' },
  { to: '/product-attributes/specifications', label: 'Specifications' },
  { to: '/product-attributes/marketplace', label: 'Marketplace mapping' },
  { to: '/product-attributes/validation', label: 'Validation' },
  { to: '/product-attributes/recommendations', label: 'AI recommendations' },
  { to: '/product-attributes/localization', label: 'Localization' },
  { to: '/product-attributes/units', label: 'Units of measure' },
];

export function ProductAttributesHubPage() {
  const [dashboard, setDashboard] = useState<AttributesDashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void productAttributesApi.dashboard().then(setDashboard).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Product Attributes</h1>
      <p className="mt-1 text-sm text-slate-600">Attribute library, specifications, validation, and AI intelligence.</p>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV.map((l) => <Link key={l.to} to={l.to} className="text-slate-700 hover:underline">{l.label}</Link>)}
      </nav>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(dashboard?.stats ?? {}).map(([k, v]) => (
          <div key={k} className="rounded-xl border bg-white p-4">
            <p className="text-sm text-slate-500">{k.replace(/_/g, ' ')}</p>
            <p className="mt-1 text-2xl font-semibold">{String(v)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AttributeLibraryPage() {
  const [items, setItems] = useState<Attribute[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void productAttributesApi.listAttributes().then(setItems).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Attribute Library</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-600">
          <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b">
              <td className="px-4 py-3 font-medium">{a.name}</td>
              <td className="px-4 py-3">{a.code}</td>
              <td className="px-4 py-3">{a.type}</td>
              <td className="px-4 py-3">{a.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AttributeBuilderPage() {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<AttributeType>('text');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const create = async () => {
    if (!code.trim() || !name.trim()) return;
    try {
      await productAttributesApi.createAttribute({ code: code.trim(), name: name.trim(), type });
      setSuccess('Attribute created');
      setCode('');
      setName('');
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Attribute Builder</h1>
      <div className="mt-6 space-y-3 rounded-xl border bg-white p-6">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <select value={type} onChange={(e) => setType(e.target.value as AttributeType)} className="w-full rounded-lg border px-3 py-2 text-sm">
          {(['text', 'number', 'boolean', 'select', 'measurement', 'compliance', 'seo'] as AttributeType[]).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-emerald-700">{success}</p>}
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create attribute</button>
      </div>
    </div>
  );
}
